<template>
    <div>
        <v-col cols="12">
            <h2 class="mt-4 mb-0">{{ t('notifications::page.title.notifications') }}</h2>
            <div>
                <b-datatable
                    ref="table"
                    :key="datatableKey"
                    :columns="columns"
                    :endpoint="listRoute"
                    :hidden-columns="hiddenColumns"
                    :initial-filters="initialFilters"
                    has-filter-selector
                    item-key="id"
                    notification
                >
                    <template #filters="{ filters }">
                        <v-col cols="4">
                            <b-filter-input
                                v-model="filters.filters.search"
                                :label="t('notifications::grid.label.search')"
                            ></b-filter-input>
                        </v-col>
                        <v-col cols="4">
                            <b-filter-select
                                v-model="filters.filters.type"
                                :items="filtersOptions.type"
                                :label="t('notifications::grid.label.type')"
                                :clearable="filters.filters.type !== null"
                            >
                            </b-filter-select>
                        </v-col>
                        <v-col cols="4">
                            <b-filter-select
                                v-model="filters.filters.status"
                                :items="filtersOptions.status"
                                :label="t('notifications::grid.label.status')"
                                :clearable="filters.filters.status !== null"
                            >
                            </b-filter-select>
                        </v-col>
                        <v-col cols="4">
                            <b-filter-date
                                v-model="filters.filters.filter_created_at"
                                range
                                :label="t('audit-logs::filters.label.date')"
                                @clear="onCloseDateFilter(filters, 'filter_created_at')"
                            ></b-filter-date>
                        </v-col>
                    </template>

                    <template #[`item.actions`]="{ item }">
                        <div class="table-actions">
                            <v-tooltip
                                v-if="can('notifications.export-record-as-pdf')"
                                top
                                transition="v-fab-transition"
                            >
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="#FFFFFF56"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="exportRecordAsPdf(item)"
                                    >
                                        <v-icon size="24">mdi-file-export-outline</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.export_record_as_pdf') }}</span>
                            </v-tooltip>

                            <v-tooltip v-if="can('notifications.get')" top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn color="#FFFFFF56" icon v-bind="attrs" v-on="on" @click="preview(item)">
                                        <v-icon>mdi-eye</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('notifications::button.details') }}</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template #[`item.created_at`]="{ item }">
                        {{
                            item.created_at
                                ? moment(item.created_at).format($root.dateFormatWithHours)
                                : t('default::label.na')
                        }}
                    </template>

                    <template #[`item.completed_by.name`]="{ item }">
                        <v-avatar
                            v-if="item.completed_by && item.completed_by.name"
                            :color="setColor(item.type)"
                            size="36"
                            class="patient-avatar mr-2"
                        >
                            <span class="white--text headline"
                                ><span class="small">{{ $root.makeInitials(`${item.completed_by.name}`) }}</span></span
                            >
                        </v-avatar>
                        {{ item.completed_by ? item.completed_by.name : '-' }}
                    </template>

                    <template #[`item.type`]="{ item }">
                        {{ t('notifications::types.' + item.type) }}
                    </template>

                    <template #[`item.status`]="{ item }">
                        <div>
                            <div class="grid-status">
                                <span>
                                    {{ t('notifications::statuses.' + item.status) }}
                                </span>
                            </div>
                        </div>
                    </template>
                </b-datatable>
            </div>
        </v-col>
        <notification-modal
            :dialog.sync="previewModal"
            @close-dialog="previewModal = false"
            @completed="handleComplete"
        ></notification-modal>
    </div>
</template>

<script>
let NotificationApprovalRequest = require(process.env.MIX_APP_ROOT +
    '/bgo/bgo-components/resources/js/mixins/Notification.js').default;
export default {
    mixins: [NotificationApprovalRequest],
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            hiddenColumns: ['id'],
            previewModal: false,
            downloadingFile: false,
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('notifications::grid.label.created_at'),
                    value: 'created_at',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('notifications::grid.label.created_by'),
                    value: 'created_by.name',
                    sortable: true,
                    width: 140,
                    maxWidth: 140,
                },
                // {
                //     text: t('notifications::grid.label.completed_by'),
                //     value: 'completed_by.name',
                //     sortable: true,
                // },
                {
                    text: t('notifications::grid.label.type'),
                    value: 'type',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('notifications::grid.label.memo'),
                    value: 'memo',
                    sortable: true,
                    width: 360,
                },
                {
                    text: t('notifications::grid.label.status'),
                    value: 'status',
                    width: 120,
                    sortable: true,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: 'action_data,status',
                    align: 'end',
                    width: 120,
                },
            ],
            filtersOptions: {
                status: [
                    {
                        text: t('notifications::statuses.all'),
                        value: null,
                    },
                    {
                        text: t('notifications::statuses.new'),
                        value: 'new',
                    },
                    {
                        text: t('notifications::statuses.completed'),
                        value: 'completed',
                    },
                ],
                type: [],
            },
            initialFilters: {
                type: null,
                status: null,
            },
        };
    },
    created() {
        axios.get(route('api.v1.notifications.config')).then((response) => {
            this.filtersOptions.type = _.union(
                [
                    {
                        text: t('notifications::statuses.all'),
                        value: null,
                    },
                ],
                response.data.notification_types
            );
        });
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('notifications.export-record-as-pdf', { notification: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        onCloseDateFilter(filters, filter) {
            switch (filter) {
                case 'filter_created_at':
                    filters.filters.created_at = null;
                    filters.filters.filter_created_at = null;
                    break;
                default:
                    break;
            }
        },
        handleComplete(id) {
            const items = this.$refs.table.$refs.vdatatable.items;
            const index = items.findIndex((item) => item.id === id);
            if (index !== -1) {
                items[index].status = 'completed';
                const row = this.$refs.table.$refs.vdatatable.$el.querySelector('tbody').childNodes[index];
                row.classList.remove('new');
                row.classList.add('completed');
            }
        },
    },
};
</script>
